import { Controller } from "@hotwired/stimulus"

// Click-to-call for a server-rendered phone link — the HAML twin of the React
// SoftphonePhoneLink (#9837 P0 item 3, click-to-call everywhere). Rendered by
// ApplicationHelper#softphone_tel_to:
//
//   <a href="tel:+15551234567" data-controller="softphone-dial"
//      data-action="click->softphone-dial#dial"
//      data-softphone-dial-phone-value="+15551234567"
//      data-softphone-dial-name-value="Jane Lead"
//      data-softphone-dial-lead-id-value="42">
//
// On connect it asks the GOAT Connect softphone for its capabilities over the
// document bridge (the same events app/frontend's softphoneDocumentBridge.ts
// defines). Only when a mounted softphone has answered does a click take the
// number and hand it over; otherwise the browser follows the tel: href, which
// is exactly the pre-softphone behaviour. The softphone owns everything after
// the handoff (its dial preflight re-resolves the lead server-side and shows
// any refusal on the dialpad), so this controller carries no authorization
// logic. Setup required: the click fronts the setup card with the number in
// the keypad (#11776) instead of dispatching a dial it would only refuse.
//
// The event names and the refusal sentences are mirrored from app/frontend
// (softphoneDocumentBridge.ts / useSoftphoneCapabilities.ts): this bundle is
// built by esbuild and cannot import the Vite one, so a change to either side
// must be made on both.
const CAPABILITY_REQUEST_EVENT = "goat:softphone-capability-request"
const CAPABILITY_EVENT = "goat:softphone-capability"
const DIAL_REQUEST_EVENT = "goat:softphone-dial-request"
const DIAL_RESULT_EVENT = "goat:softphone-dial-result"
const KEYPAD_REQUEST_EVENT = "goat:softphone-keypad-request"
const KEYPAD_RESULT_EVENT = "goat:softphone-keypad-result"
const HANDOFF_TIMEOUT_MS = 1500

const FAILURE_MESSAGES = {
  busy: "Another GOAT Connect call is already in progress.",
  missing_caller_id: "Outbound caller ID is not configured.",
  provider_error: "GOAT Connect could not start the call.",
  // The softphone refused for a reason of its own (#15453); its sentence
  // (`message`) normally wins — this is the fallback when it sent none.
  refused: "GOAT Connect could not start the call.",
  unmounted: "GOAT Connect is unavailable. Refresh and try again.",
}

// Reasons whose softphone sentence (`message`) is spoken instead of the fixed
// copy — the same two dialFailureMessage (useSoftphoneCapabilities.ts) speaks
// it for, so a refusal toasts exactly what the dialpad says (#15588).
const MESSAGE_REASONS = ["refused", "missing_caller_id"]

let requestSequence = 0

function nextRequestId(prefix) {
  requestSequence += 1
  return `${prefix}-${Date.now()}-${requestSequence}`
}

function failureMessage(reason, message = null) {
  if (MESSAGE_REASONS.includes(reason) && typeof message === "string" && message.trim() !== "") return message
  return FAILURE_MESSAGES[reason] || FAILURE_MESSAGES.unmounted
}

// Mirrors lib/avatarColor.ts getInitials — the softphone's contact card
// avatar reads these.
function initialsFor(name) {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return "?"
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function toast(message, tone) {
  if (typeof window.showToast === "function") window.showToast(message, tone)
}

export default class extends Controller {
  static values = { phone: String, name: String, leadId: Number }

  connect() {
    this.capabilities = null
    this.onCapability = (event) => {
      const detail = event.detail
      if (!detail || typeof detail.mounted !== "boolean") return
      this.capabilities = detail
    }
    document.addEventListener(CAPABILITY_EVENT, this.onCapability)
    document.dispatchEvent(new CustomEvent(CAPABILITY_REQUEST_EVENT, {
      detail: { requestId: nextRequestId("capability") },
    }))
  }

  disconnect() {
    document.removeEventListener(CAPABILITY_EVENT, this.onCapability)
  }

  dial(event) {
    // The click is the link's, never a hosting row's or card's — the same
    // unconditional stop the React twin (useSoftphoneDial#onClick) makes.
    event.stopPropagation()
    // No softphone answered: the tel: href is the call.
    if (!this.capabilities || !this.capabilities.mounted) return

    event.preventDefault()
    const number = this.phoneValue.replace(/\D/g, "")
    if (!number) return

    if (!this.capabilities.callerIdConfigured) {
      this.request(KEYPAD_REQUEST_EVENT, KEYPAD_RESULT_EVENT, { number }).then((result) => {
        // A successful route reads as guidance (info) — the readiness sentence
        // says why the phone came forward; only a refused one reads as red.
        if (result.accepted) toast(this.setupMessage(), "bg-info")
        else toast(failureMessage(result.reason), "bg-danger")
      })
      return
    }

    const name = this.nameValue.trim() || this.phoneValue
    const contact = {
      // `lead-<id>` is what the softphone turns back into the lead the call
      // is coaching on; anything else is a plain number.
      id: this.hasLeadIdValue && this.leadIdValue > 0 ? `lead-${this.leadIdValue}` : `phone-${number}`,
      name,
      number,
      initials: initialsFor(name),
    }
    this.request(DIAL_REQUEST_EVENT, DIAL_RESULT_EVENT, { contact }).then((result) => {
      // A refused handoff must SPEAK — silence reads as "it's ringing".
      if (!result.accepted) toast(this.dialFailureMessage(result), "bg-danger")
    })
  }

  // A missing caller ID with no softphone sentence falls back to the server's
  // readiness detail, as dialFailureMessage does in the React twin.
  dialFailureMessage(result) {
    if (result.reason === "missing_caller_id" && !(typeof result.message === "string" && result.message.trim() !== "")) {
      return this.setupMessage()
    }
    return failureMessage(result.reason, result.message)
  }

  setupMessage() {
    const readiness = this.capabilities.readiness
    return readiness && readiness.ready === false && readiness.detail
      ? readiness.detail
      : FAILURE_MESSAGES.missing_caller_id
  }

  // Dispatches one request on the document bridge and resolves with the
  // matching result, or `unmounted` when nothing answers in time.
  request(requestEvent, resultEvent, detail) {
    const requestId = nextRequestId(requestEvent === DIAL_REQUEST_EVENT ? "dial" : "keypad")

    return new Promise((resolve) => {
      let settled = false
      let timeoutId

      const finish = (result) => {
        if (settled) return
        settled = true
        window.clearTimeout(timeoutId)
        document.removeEventListener(resultEvent, onResult)
        resolve(result)
      }
      const onResult = (event) => {
        const result = event.detail
        if (result && result.requestId === requestId) finish(result)
      }

      document.addEventListener(resultEvent, onResult)
      timeoutId = window.setTimeout(() => finish({ requestId, accepted: false, reason: "unmounted" }), HANDOFF_TIMEOUT_MS)
      document.dispatchEvent(new CustomEvent(requestEvent, { detail: { requestId, ...detail } }))
    })
  }
}
